import _ from 'lodash';
import {
  Alert, Button, Form, Icon, Input, Result, Spin,
} from 'antd';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import * as actions from '../actions';
import './SAWWCSetup.css';

// Backend status vocabulary emitted on the `sawwc-create` status key.
// (see py/api backend + js headless_processor/src/targets/sawwc.js)
const SETUP_STATUS = {
  failed: 'failed',
  finished: 'finished',
  loading: 'loading',
  initiated: 'initiated',
  started: 'started',
  running: 'running',
  waiting: 'waiting',
};

// Wizard steps that are driven purely by the UI (before the backend job exists).
const STEP = {
  intro: 'intro',
  credentials: 'credentials',
  processing: 'processing',
};

// Statuses that mean a setup attempt is actively running on the backend.
const LIVE_STATUSES = [
  SETUP_STATUS.loading,
  SETUP_STATUS.initiated,
  SETUP_STATUS.started,
  SETUP_STATUS.running,
  SETUP_STATUS.waiting,
];

const isLiveStatus = status => LIVE_STATUSES.includes(status);
const isTerminalStatus = status => status === SETUP_STATUS.finished || status === SETUP_STATUS.failed;

const preventBrowserAutocomplete = event => {
  if (!event.target.value) {
    event.target.type = 'text';
  } else {
    event.target.type = 'password';
  }
};

@Form.create()
class SAWWCCredentialsFormComponent extends PureComponent {
  handleSubmit = e => {
    e.preventDefault();
    this.props.form.validateFields((err, values) => {
      if (err) return;
      this.props.onSubmit({ username: values.username, password: values.password });
    });
  }

  render() {
    const { form, submitting } = this.props;
    const { getFieldDecorator } = form;
    return (
      <Form layout="vertical" onSubmit={this.handleSubmit}>
        <div className="sawwc-setup__step-header">
          <h3>Enter your SA DHS credentials</h3>
          <p>
            This process will create an Oho user in your SA DHS portal. Enter the
            username and password of an SA DHS administrator account below.
          </p>
        </div>
        <Form.Item label="SA DHS Username">
          {getFieldDecorator('username', {
            rules: [{ required: true, message: 'Please enter your SA DHS username' }],
          })(<Input placeholder="Your SA DHS portal username" autoComplete="off" />)}
        </Form.Item>
        <Form.Item label="SA DHS Password">
          {getFieldDecorator('password', {
            rules: [{ required: true, message: 'Please enter your SA DHS password' }],
          })(
            <Input
              type="text"
              placeholder="Your SA DHS portal password"
              onChange={preventBrowserAutocomplete}
            />,
          )}
        </Form.Item>
        <div className="sawwc-setup__hint">
          <Icon type="lock" />
          <span>Your credentials are used only to authorise the setup and are never stored by Oho.</span>
        </div>
        <div className="sawwc-setup__actions">
          <Button className="sawwc-setup__btn-ghost" onClick={this.props.onCancel}>Back</Button>
          <Button className="sawwc-setup__btn-primary" htmlType="submit" loading={submitting}>Continue</Button>
        </div>
      </Form>
    );
  }
}

@Form.create()
class SAWWCOtpFormComponent extends PureComponent {
  handleSubmit = e => {
    if (e) e.preventDefault();
    this.props.form.validateFields((err, values) => {
      if (err) return;
      this.props.onSubmit(values.otp);
    });
  }

  render() {
    const { form, submitting } = this.props;
    const { getFieldDecorator } = form;
    const handleKeyUp = evt => {
      if (evt.key === 'Enter') this.handleSubmit();
    };
    return (
      <Form layout="vertical" style={{ paddingTop: '4px' }}>
        <div className="sawwc-setup__step-header">
          <h3>One-time password required</h3>
          <p>Please enter the one-time password sent to your SA DHS account, once you have received it.</p>
        </div>
        <Form.Item label="One-Time Password">
          {getFieldDecorator('otp', {
            rules: [{ required: true, message: 'Please enter your one-time password' }],
          })(<Input onKeyUp={handleKeyUp} autoComplete="off" size="large" placeholder="Enter code" />)}
        </Form.Item>
        <div className="sawwc-setup__actions sawwc-setup__actions--no-border">
          <Button className="sawwc-setup__btn-primary" loading={submitting} onClick={() => this.handleSubmit()}>Submit</Button>
        </div>
      </Form>
    );
  }
}

export class SAWWCSetupComponent extends PureComponent {
  state = {
    step: STEP.intro,
    isSubmitting: false,
    isResuming: true,
    pollerStatus: '',
    pollerMessage: '',
    errorMessage: '',
    otpSubmitted: false,
    // While true, terminal statuses (failed/finished) are ignored because the
    // sawwc-create redis key can still hold a STALE terminal value from a prior
    // attempt until the worker writes the first fresh update for this attempt.
    awaitingFresh: false,
  }

  constructor(props) {
    super(props);
    this.pollerRef = null;
  }

  async componentDidMount() {
    // The backend has no "is a setup in progress" probe, so we infer it from a
    // single status poll: a live status means an attempt is already running and
    // we resume straight into the processing step. A terminal/empty status is
    // treated as "no active session" (it may be stale from a prior run).
    const result = await this.props.checkSAWWCSetupStatus();
    const status = _.get(result, ['status'], '');
    const message = _.get(result, ['message'], '');
    if (isLiveStatus(status)) {
      this.setState({
        step: STEP.processing,
        pollerStatus: status,
        pollerMessage: message,
        awaitingFresh: false,
        isResuming: false,
      });
      this.startPolling();
    } else {
      this.setState({ isResuming: false });
    }
  }

  componentWillUnmount() {
    this.stopPolling();
    this.props.updateSAWWCSetupStatusToDefault();
  }

  componentDidUpdate(prevProps) {
    if (this.state.step !== STEP.processing) return;
    const { sawwcSetupResponse } = this.props;
    if (prevProps.sawwcSetupResponse === sawwcSetupResponse) return;

    const status = _.get(sawwcSetupResponse, ['message', 'status'], '');
    const message = _.get(sawwcSetupResponse, ['message', 'message'], '');

    // Ignore a stale terminal status left over from a previous attempt until the
    // worker has emitted a fresh live status for the current attempt.
    if (this.state.awaitingFresh) {
      if (!isLiveStatus(status)) return;
      this.setState({ awaitingFresh: false });
    }

    const error = status === SETUP_STATUS.failed ? (message || 'SA WWC user creation failed') : '';
    const nextState = { pollerStatus: status, pollerMessage: message, errorMessage: error };
    // Once the backend moves past "waiting" (OTP consumed), allow the OTP form
    // to be shown again if a later step ever requests another code.
    if (this.state.otpSubmitted && status !== SETUP_STATUS.waiting) nextState.otpSubmitted = false;
    this.setState(nextState);

    if (isTerminalStatus(status)) this.stopPolling();

    // On success, refresh the org so providers.sawwc.dhs_user is populated and
    // the setup shows as completed. Guard so it only fires once per completion.
    if (status === SETUP_STATUS.finished && this.state.pollerStatus !== SETUP_STATUS.finished
      && this.props.onCompleted) {
      this.props.onCompleted();
    }
  }

  // providers.sawwc.dhs_user being set means SA WWC is already configured.
  isAlreadySetup = () => {
    const { settings } = this.props;
    const providers = _.get(settings, ['providers'], {});
    return !_.isNil(_.get(providers, ['sawwc', 'dhs_user'], null));
  }

  startPolling = () => {
    this.stopPolling();
    this.props.checkSAWWCSetupStatus();
    this.pollerRef = setInterval(() => {
      this.props.checkSAWWCSetupStatus();
    }, 5000);
  }

  stopPolling = () => {
    if (this.pollerRef) {
      clearInterval(this.pollerRef);
      this.pollerRef = null;
    }
  }

  handleContinueFromIntro = () => this.setState({ step: STEP.credentials });

  handleSubmitCredentials = async ({ username, password }) => {
    this.setState({ isSubmitting: true });
    const action = await this.props.startSAWWCCreateUser({ username, password });
    this.setState({ isSubmitting: false });
    const failed = !action || action.failed || (action.data && !action.data.success);
    if (failed) return;
    // Enter processing and ignore any stale terminal status still sitting in the
    // sawwc-create key until the worker emits a fresh live status for this run.
    this.setState({
      step: STEP.processing,
      pollerStatus: SETUP_STATUS.loading,
      pollerMessage: '',
      errorMessage: '',
      awaitingFresh: true,
    });
    this.startPolling();
  }

  handleSubmitOtp = async otp => {
    this.setState({ isSubmitting: true });
    const action = await this.props.postSAWWCOtp(otp);
    this.setState({ isSubmitting: false });
    if (!action || action.failed || !(action.data && action.data.saved)) return;
    // Backend is now processing the OTP. The status may still read "waiting"
    // briefly until the worker flips to "running"; suppress the OTP form and
    // show a spinner in the meantime.
    this.setState({ pollerStatus: SETUP_STATUS.loading, otpSubmitted: true });
  }

  renderAlreadySetup = () => (
    <Result
      status="success"
      title="SA WWC is already set up"
      subTitle="Your organisation already has an Oho user configured in the SA DHS portal. There is nothing more to do here."
      extra={<Button type="primary" onClick={this.props.onClose}>Close</Button>}
    />
  )

  renderIntro = () => (
    <div>
      <div className="sawwc-setup__intro-card">
        <span className="sawwc-setup__icon-badge">
          <Icon type="safety-certificate" theme="filled" />
        </span>
        <div className="sawwc-setup__intro-body">
          <h4>Automated SA DHS user creation</h4>
          <p>
            This setting will automate the creation of an Oho user in your SA DHS portal.
            Only users who are SA DHS administrators can complete this process.
          </p>
        </div>
      </div>
      <div className="sawwc-setup__actions">
        <Button className="sawwc-setup__btn-ghost" onClick={this.props.onClose}>Cancel</Button>
        <Button className="sawwc-setup__btn-primary" onClick={this.handleContinueFromIntro}>Continue</Button>
      </div>
    </div>
  )

  renderCredentials = () => (
    <SAWWCCredentialsFormComponent
      submitting={this.state.isSubmitting}
      onSubmit={this.handleSubmitCredentials}
      onCancel={() => this.setState({ step: STEP.intro })}
    />
  )

  renderProcessing = () => {
    const { pollerStatus, pollerMessage, errorMessage } = this.state;

    if (pollerStatus === SETUP_STATUS.finished) {
      return (
        <Result
          status="success"
          title="SA WWC setup complete"
          subTitle="Your Oho user has been created in the SA DHS portal."
          extra={<Button type="primary" onClick={this.props.onClose}>Done</Button>}
        />
      );
    }

    if (pollerStatus === SETUP_STATUS.failed) {
      return (
        <div>
          <Alert type="error" showIcon message="SA WWC setup failed" description={errorMessage} />
          <div className="sawwc-setup__actions">
            <Button className="sawwc-setup__btn-ghost" onClick={this.props.onClose}>Close</Button>
            <Button
              className="sawwc-setup__btn-primary"
              onClick={() => {
                this.stopPolling();
                this.setState({
                  step: STEP.credentials, pollerStatus: '', pollerMessage: '', errorMessage: '', otpSubmitted: false, awaitingFresh: false,
                });
              }}
            >Try again</Button>
          </div>
        </div>
      );
    }

    if (pollerStatus === SETUP_STATUS.waiting && !this.state.isSubmitting && !this.state.otpSubmitted) {
      return (
        <SAWWCOtpFormComponent submitting={this.state.isSubmitting} onSubmit={this.handleSubmitOtp} />
      );
    }

    // loading / initiated / started / running (and post-OTP submitting)
    return (
      <div className="sawwc-setup__processing">
        <Spin size="large" className="sawwc-setup__spinner" />
        <p className="sawwc-setup__processing-status">
          {pollerMessage
            ? <span><strong>Status:</strong> {pollerMessage}</span>
            : 'Starting the setup process…'}
        </p>
      </div>
    );
  }

  // Which wizard step (0-based) is currently active, for the progress rail.
  activeStepIndex = () => {
    if (this.state.step === STEP.intro) return 0;
    if (this.state.step === STEP.credentials) return 1;
    return 2;
  }

  renderStepRail = () => {
    const active = this.activeStepIndex();
    const isTerminal = this.state.pollerStatus === SETUP_STATUS.finished
      || this.state.pollerStatus === SETUP_STATUS.failed;
    const dots = [0, 1, 2];
    return (
      <div className="sawwc-setup__steps">
        {dots.map((i, idx) => {
          const done = i < active || (i === 2 && isTerminal);
          const isActive = i === active && !done;
          const dotClass = ['sawwc-setup__step-dot'];
          if (done) dotClass.push('sawwc-setup__step-dot--done');
          else if (isActive) dotClass.push('sawwc-setup__step-dot--active');
          return (
            <React.Fragment key={i}>
              <span className={dotClass.join(' ')}>
                {done ? <Icon type="check" /> : i + 1}
              </span>
              {idx < dots.length - 1 && (
                <span className={`sawwc-setup__step-line${i < active ? ' sawwc-setup__step-line--done' : ''}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  render() {
    if (this.isAlreadySetup()) {
      return <div className="sawwc-setup">{this.renderAlreadySetup()}</div>;
    }

    // Avoid flashing the intro while the mount poll decides whether to resume.
    if (this.state.isResuming) {
      return (
        <div className="sawwc-setup">
          <div className="sawwc-setup__processing">
            <Spin size="large" className="sawwc-setup__spinner" />
            <p className="sawwc-setup__processing-status">Checking setup status…</p>
          </div>
        </div>
      );
    }

    let body;
    if (this.state.step === STEP.intro) body = this.renderIntro();
    else if (this.state.step === STEP.credentials) body = this.renderCredentials();
    else body = this.renderProcessing();

    return (
      <div className="sawwc-setup">
        {this.renderStepRail()}
        {body}
      </div>
    );
  }
}

export const SAWWCSetup = connect(
  state => ({
    sawwcSetupResponse: state.doc.getIn(['sawwcSetup'], false),
  }),
  actions,
)(SAWWCSetupComponent);
